import { useMediaQuery } from '../../hooks/useMediaQuery';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { type Doctor } from './types';
import { useTheme } from '../../theme/ThemeContext';

export type CalendarView = 'day' | 'week';
export type CalendarTransfer = 'upload' | 'download' | null;

export interface GoogleCalendarOption {
  id: string;
  name: string;
  isPrimary: boolean;
}

interface CalendarToolbarProps {
  canManageGoogle?: boolean;
  /** 'sheet' renders only the actions and filters, for the phone options sheet. */
  variant?: 'toolbar' | 'sheet';
  view: string;
  onViewChange: (view: CalendarView) => void;
  onToday: () => void;
  onNewEvent: () => void;
  calendarTransfer: CalendarTransfer;
  onUploadToGoogle: () => void;
  onDownloadFromGoogle: () => void;
  calendarEmail: string | null;
  selectedCalendarId: string | null;
  selectedCalendarName: string | null;
  calendarList: GoogleCalendarOption[];
  showCalendarPicker: boolean;
  onOpenCalendarPicker: () => void;
  onCloseCalendarPicker: () => void;
  onSelectCalendar: (id: string, name: string) => void;
  onReconnectGoogle: () => void;
  doctors: Doctor[];
  filterDoctorId: string;
  filterDoctorId2: string;
  filterRoom: string;
  showCanceledOrNoShow: boolean;
  onFilterDoctorIdChange: (value: string) => void;
  onFilterDoctorId2Change: (value: string) => void;
  onFilterRoomChange: (value: string) => void;
  onShowCanceledOrNoShowChange: (value: boolean) => void;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
}

const actionButtonStyle: React.CSSProperties = {
  padding: '6px 12px',
  border: 'none',
  color: '#f1f5f9',
  borderRadius: '6px',
  fontWeight: 500,
  fontSize: '14px'
};

const filterStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: '6px',
  border: '1px solid #475569',
  fontSize: '14px',
  outline: 'none',
  background: '#1e293b',
  color: '#f1f5f9'
};

export const CalendarToolbar: React.FC<CalendarToolbarProps> = ({
  canManageGoogle = false,
  variant = 'toolbar',
  view,
  onViewChange,
  onToday,
  onNewEvent,
  calendarTransfer,
  onUploadToGoogle,
  onDownloadFromGoogle,
  calendarEmail,
  selectedCalendarId,
  selectedCalendarName,
  calendarList,
  showCalendarPicker,
  onOpenCalendarPicker,
  onCloseCalendarPicker,
  onSelectCalendar,
  onReconnectGoogle,
  doctors,
  filterDoctorId,
  filterDoctorId2,
  filterRoom,
  showCanceledOrNoShow,
  onFilterDoctorIdChange,
  onFilterDoctorId2Change,
  onFilterRoomChange,
  onShowCanceledOrNoShowChange,
  searchQuery,
  onSearchQueryChange
}) => {
  const { t } = useTranslation();
  const isPhone = useMediaQuery('(max-width: 767px)');
  const { tokens } = useTheme();
  const transferInProgress = calendarTransfer !== null;
  const colors = {
    surface: tokens.app.surface,
    surfaceAlt: tokens.app.surfaceAlt,
    active: tokens.app.activeBg,
    activeText: tokens.app.activeText,
    text: tokens.app.text,
    muted: tokens.app.muted,
    border: tokens.app.inputBorder,
    blue: tokens.app.primary,
    disabled: tokens.app.border,
    shadow: tokens.app.shadow,
    hover: tokens.app.subtle
  };
  const themedFilterStyle: React.CSSProperties = {
    ...filterStyle,
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    color: colors.text
  };

  const isSheet = variant === 'sheet';
  const optionsOpen = isSheet ? true : isPhone ? undefined : true;

  return (
    <div className={isSheet ? 'calendar-toolbar calendar-toolbar-sheet' : 'calendar-toolbar'} style={isSheet
      ? { display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '8px' }
      : { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', background: colors.surface, padding: '12px', borderRadius: '8px', border: `1px solid ${colors.border}`, boxShadow: colors.shadow }}>
      {!isSheet && <>
      <div style={{ display: 'flex', background: colors.surfaceAlt, borderRadius: '6px', padding: '4px' }}>
        {(['day', 'week'] as const).map(option => (
          <button
            key={option}
            onClick={() => onViewChange(option)}
            style={{
              padding: '6px 12px',
              border: 'none',
              background: view === option ? colors.active : 'transparent',
              color: view === option ? colors.activeText : colors.muted,
              borderRadius: '4px',
              fontWeight: 500,
              fontSize: '14px',
              cursor: 'pointer',
              boxShadow: view === option ? '0 1px 2px rgba(0,0,0,0.2)' : 'none',
              textTransform: 'capitalize'
            }}
          >
            {t(`calendar.${option}`)}
          </button>
        ))}
      </div>

      <button onClick={onToday} style={{ ...actionButtonStyle, background: colors.blue, color: '#fff', cursor: 'pointer' }}>
        {t('calendar.today') || 'Today'}
      </button>
      </>}

      <button onClick={onNewEvent} style={{ ...actionButtonStyle, background: colors.surfaceAlt, color: colors.text, border: `1px solid ${colors.border}`, cursor: 'pointer' }}>
        {t('calendar.newEvent', 'New event')}
      </button>

      {canManageGoogle && <details className="calendar-options" open={optionsOpen}><summary>{t('responsive.google')}</summary><div className="calendar-options-content">
      <button
        onClick={onUploadToGoogle}
        disabled={transferInProgress}
        style={{
          ...actionButtonStyle,
          background: transferInProgress ? colors.disabled : colors.blue,
          color: '#fff',
          cursor: transferInProgress ? 'not-allowed' : 'pointer',
          opacity: transferInProgress ? 0.8 : 1
        }}
        title={t('calendar.uploadGoogleTitle')}
      >
        {calendarTransfer === 'upload' ? t('calendar.uploadingGoogle') : t('calendar.uploadGoogle')}
      </button>

      <button
        onClick={onDownloadFromGoogle}
        disabled={transferInProgress}
        style={{
          ...actionButtonStyle,
          background: transferInProgress ? colors.disabled : colors.blue,
          color: '#fff',
          cursor: transferInProgress ? 'not-allowed' : 'pointer',
          opacity: transferInProgress ? 0.8 : 1
        }}
        title={t('calendar.downloadGoogleTitle')}
      >
        {calendarTransfer === 'download' ? t('calendar.downloadingGoogle') : t('calendar.downloadGoogle')}
      </button>

      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
        {calendarEmail && (
          <span style={{ fontSize: '10px', color: colors.muted, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={calendarEmail}>
            {calendarEmail}
          </span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {selectedCalendarName && (
            <span style={{ fontSize: '11px', color: colors.muted, maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={selectedCalendarId ?? ''}>
              {'\uD83D\uDCC5'} {selectedCalendarName}
            </span>
          )}
          <button onClick={onOpenCalendarPicker} title="Switch calendar" style={{ padding: '2px 6px', border: `1px solid ${colors.border}`, background: 'transparent', color: colors.muted, borderRadius: '4px', fontSize: '10px', cursor: 'pointer' }}>
            {t('calendar.changeCalendar')}
          </button>
          <button onClick={onReconnectGoogle} title="Switch Google account" style={{ padding: '2px 6px', border: `1px solid ${colors.border}`, background: 'transparent', color: colors.muted, borderRadius: '4px', fontSize: '10px', cursor: 'pointer' }}>
            {t('calendar.reconnect')}
          </button>
        </div>

        {showCalendarPicker && (
          <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 9999, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '8px', minWidth: '240px', boxShadow: colors.shadow }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: colors.text }}>{t('calendar.selectCalendar')}</span>
              <button onClick={onCloseCalendarPicker} style={{ background: 'none', border: 'none', color: colors.muted, cursor: 'pointer', fontSize: '14px' }}>{'\u2715'}</button>
            </div>
            {calendarList.map(calendar => (
              <div
                key={calendar.id}
                onClick={() => onSelectCalendar(calendar.id, calendar.name)}
                style={{
                  padding: '6px 8px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: calendar.id === selectedCalendarId ? colors.activeText : colors.text,
                  background: calendar.id === selectedCalendarId ? colors.active : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={event => (event.currentTarget.style.background = colors.hover)}
                onMouseLeave={event => (event.currentTarget.style.background = calendar.id === selectedCalendarId ? colors.active : 'transparent')}
              >
                {calendar.id === selectedCalendarId ? '\u2713 ' : '  '}{calendar.name}{calendar.isPrimary ? ' (primary)' : ''}
              </div>
            ))}
          </div>
        )}
      </div>

      </div></details>}
      <details className="calendar-options" open={optionsOpen}><summary>{t('responsive.filters')}</summary><div className="calendar-options-content">

      <select aria-label={t('calendar.doctor1')} value={filterDoctorId} onChange={event => onFilterDoctorIdChange(event.target.value)} style={themedFilterStyle}>
        <option value="">{t('calendar.doctor1')}</option>
        {doctors.map(doctor => <option key={doctor.id} value={doctor.id}>{doctor.firstName} {doctor.lastName}</option>)}
      </select>

      <select aria-label={t('calendar.doctor2')} value={filterDoctorId2} onChange={event => onFilterDoctorId2Change(event.target.value)} style={themedFilterStyle}>
        <option value="">{t('calendar.doctor2')}</option>
        {doctors.map(doctor => <option key={doctor.id} value={doctor.id}>{doctor.firstName} {doctor.lastName}</option>)}
      </select>

      <select aria-label={t('calendar.allRooms')} value={filterRoom} onChange={event => onFilterRoomChange(event.target.value)} style={themedFilterStyle}>
        <option value="">{t('calendar.allRooms')}</option>
        {[1, 2, 3, 4, 5].map(room => <option key={room} value={room}>{t('calendar.room')} {room}</option>)}
      </select>

      <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: colors.text, fontSize: 13, whiteSpace: 'nowrap', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={showCanceledOrNoShow}
          onChange={event => onShowCanceledOrNoShowChange(event.target.checked)}
        />
        {t('calendar.showCanceledOrNoShow', 'Show canceled/no-show')}
      </label>

      </div></details>

      {!isSheet && <>
      <div className="calendar-search-field" style={{ position: 'relative' }}>
        <input
          data-testid="calendar-search"
          aria-label={t('calendar.searchPlaceholder')}
          type="text"
          placeholder={t('calendar.searchPlaceholder')}
          value={searchQuery}
          onChange={event => onSearchQueryChange(event.target.value)}
          style={{ padding: '8px 12px 8px 32px', borderRadius: '6px', border: `1px solid ${colors.border}`, fontSize: '14px', outline: 'none', width: '240px', background: colors.surface, color: colors.text }}
        />
        <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: colors.muted }}>{'\uD83D\uDD0D'}</div>
      </div>

      <div className="calendar-tip" style={{ color: colors.muted, fontSize: 12, marginLeft: 8, whiteSpace: 'nowrap' }}>
        {t('calendar.tipTreatment')}
      </div>
      </>}
    </div>
  );
};
